// ===========================================
// INTEGRATIONS SERVICE - Third-party APIs
// ===========================================

import api from './api';

export interface IntegrationStatus {
  connected: boolean;
  message?: string;
  account?: Record<string, any>;
  setup?: { required?: string; method?: string; docs: string };
  oauthConfigured?: boolean;
  authorizationUrl?: string | null;
  needsReauth?: boolean;
}

export interface CalendlyEventType {
  uri: string;
  name: string;
  slug: string;
  active: boolean;
  scheduling_url: string;
  duration: number;
  kind: string;
  color: string;
  description_plain: string;
}

export interface CalendlyEvent {
  uri: string;
  name: string;
  status: 'active' | 'canceled';
  start_time: string;
  end_time: string;
  event_type: string;
  location: { type: string; location?: string };
  invitees_counter: { total: number; active: number; limit: number };
  created_at: string;
  updated_at: string;
}

export interface CalendlyInvitee {
  uri: string;
  email: string;
  name: string;
  status: 'active' | 'canceled';
  questions_and_answers: Array<{ question: string; answer: string }>;
  timezone: string;
  created_at: string;
  updated_at: string;
}

export const integrationsService = {
  // ================== CALENDLY OAUTH ==================
  async getCalendlyStatus() {
    return api.get<IntegrationStatus>('/api/integrations/calendly/status');
  },

  async getCalendlyAuthUrl() {
    return api.get<{ success: boolean; authorizationUrl: string; message: string }>('/api/integrations/calendly/oauth/authorize');
  },

  async exchangeCalendlyCode(code: string) {
    return api.post<{ success: boolean; message: string; expiresIn: number }>('/api/integrations/calendly/oauth/token', { code });
  },

  async disconnectCalendly() {
    return api.post<{ success: boolean; message: string }>('/api/integrations/calendly/disconnect', {});
  },

  // ================== CALENDLY DATA ==================
  async listCalendlyEventTypes() {
    return api.get<{ success: boolean; eventTypes: CalendlyEventType[] }>('/api/integrations/calendly/event-types');
  },

  async listCalendlyScheduledEvents() {
    return api.get<{ success: boolean; events: CalendlyEvent[] }>('/api/integrations/calendly/scheduled-events');
  },

  async getCalendlySchedulingLink() {
    return api.get<{ success: boolean; schedulingUrl: string; embedCode: string }>('/api/integrations/calendly/scheduling-link');
  },

  async getCalendlyEvent(uuid: string) {
    return api.get<{ success: boolean; event: CalendlyEvent; invitees: CalendlyInvitee[] }>(`/api/integrations/calendly/event/${uuid}`);
  },

  async cancelCalendlyEvent(uuid: string, reason?: string) {
    return api.post<{ success: boolean; message: string }>(`/api/integrations/calendly/cancel/${uuid}`, { reason });
  },

  async createCalendlyWebhook(callbackUrl?: string) {
    return api.post<{ success: boolean; webhook: any; message: string }>('/api/integrations/calendly/create-webhook', { callbackUrl });
  },

  async listCalendlyWebhooks() {
    return api.get<{ success: boolean; webhooks: any[] }>('/api/integrations/calendly/webhooks');
  },

  // ================== ALL INTEGRATIONS STATUS ==================
  async getAllIntegrationsStatus() {
    return api.get<{ success: boolean; integrations: Record<string, any>; totalConfigured: number; totalAvailable: number }>('/api/integrations/status');
  },

  // ================== SENDGRID ==================
  async getSendGridStatus() {
    return api.get<IntegrationStatus>('/api/integrations/sendgrid/status');
  },

  // ================== TWILIO ==================
  async getTwilioStatus() {
    return api.get<IntegrationStatus>('/api/integrations/twilio/status');
  },

  // ================== STRIPE ==================
  async getStripeConfig() {
    return api.get<{ success: boolean; publishableKey: string; currency: string }>('/api/payments/config');
  },
};

export default integrationsService;
